import { useVariant } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { Button } from '@calendly/ui/components/button';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseIcon } from '@calendly/ui/components/icons';
import { Modal } from '@calendly/ui/components/modal';
import { Sheet } from '@calendly/ui/components/sheet';
import { HeadingText, BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useEffect, useState, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useUpdateUserActivationDataMutation } from 'common';
import { moveToCheckout } from 'features/billing/actions/create_subscription';
import { setAnalyticsLocation } from 'features/billing/analytics_slice';
import { MODAL_NAMES, BILLING_ENTRYPOINTS, getModalName } from 'features/billing/constants';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { useTrackOnce, useTrack } from 'hooks/analytics_events/useTrack';
import { CalendlyLogo } from '../trial_lifecycle_modal';
import { useGetFeatureUsageQuery } from './api';
import TrialExpired from './assets/trial_expired.svg';
import TrialExpiring from './assets/trial_expiring.svg';
import { isValueEmpty } from './common';
import { FeatureGrid } from './feature_grid';
const wizardIsPhoneCss = "wrnqetl";
const wizardCss = "wyawsvk";
const Wizard = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <Sheet ref={ref} {...props} className={classNames(wizardCss, {
    [wizardIsPhoneCss]: isPhone
  }, props.className)} />;
});
const headerCss = "htpv847";
const Header = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerCss, props.className)} />;
});
const calendlyLogoImgCss = "cnd85mu";
const CalendlyLogoImg = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  return <Component ref={ref} {...restProps} className={classNames(calendlyLogoImgCss, props.className)} />;
});
const closeButtonCss = "czdfmhy";
const CloseButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(closeButtonCss, props.className)} />);
const mobileCloseButtonCss = "m7cutuq";
const MobileCloseButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(mobileCloseButtonCss, props.className)} />);
const bodyCss = "b1etnar2";
const Body = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bodyCss, props.className)} />;
});
const trialStatusImgIsTabletUpCss = "tssjxag";
const trialStatusImgCss = "tblmgpa";
const TrialStatusImg = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  const isTabletUp = useDeviceType('isTabletUp');
  return <Component ref={ref} {...restProps} className={classNames(trialStatusImgCss, {
    [trialStatusImgIsTabletUpCss]: isTabletUp
  }, props.className)} />;
});
const titleContainerIsPhoneCss = "t1oovo3b";
const titleContainerIsTabletUpCss = "tc9im1i";
const titleContainerIsSmallPhoneCss = "t1laxil3";
const titleContainerCss = "t1c9n7d4";
const TitleContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  const isSmallPhone = useDeviceType('isSmallPhone');
  const isTabletUp = useDeviceType('isTabletUp');
  return <Component ref={ref} {...restProps} className={classNames(titleContainerCss, {
    [titleContainerIsPhoneCss]: isPhone,
    [titleContainerIsTabletUpCss]: isTabletUp,
    [titleContainerIsSmallPhoneCss]: isSmallPhone
  }, props.className)} />;
});
const buttonContainerCss = "bhmduj0";
const ButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonContainerCss, props.className)} />;
});
const compareButtonCss = "c15lk0i2";
const CompareButton = forwardRef((props, ref) => <Button ref={ref} {...props} className={classNames(compareButtonCss, props.className)} />);
const continueButtonCss = "csqqdcm";
const ContinueButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(continueButtonCss, props.className)} />);
const ModalComponent = ({
  featureData,
  isTrialActive,
  trialEndsInDays
}) => {
  const dispatch = useDispatch();
  const track = useTrack();
  const [update] = useUpdateUserActivationDataMutation();
  const isPhone = useDeviceType('isPhone');
  const isSmallPhone = window.innerWidth <= 375;
  const [isOpen, setIsOpen] = useState(true);
  const {
    pathname
  } = useLocation();
  const analyticsLocation = BILLING_ENTRYPOINTS.TRIAL_LOSS_MODAL;
  const isTrialLossExperimentActive = useVariant('growth_full_page_trial_expiration', true) === 'variant';
  const forceCtaInteraction = useVariant('growth_force_upgrade', true) === 'variant';
  const modalName = getModalName({
    isTrialActive,
    forceCtaInteraction,
    trialEndsInDays
  });
  useEffect(() => {
    if (!isTrialLossExperimentActive) {
      setIsOpen(false);
    }
  }, [isTrialLossExperimentActive]);
  const userEnabledFeatures = Object.keys(featureData).filter(feature => {
    const value = featureData[feature];
    if (!value) return false;
    return !((Array.isArray(value) || typeof value === 'string') && value.length === 0);
  });
  useTrackOnce(isOpen && isTrialLossExperimentActive, {
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      action: 'opening',
      location: analyticsLocation,
      modal_name: modalName,
      path: pathname
    }
  });
  const handleClose = () => {
    if (isTrialActive) {
      update({
        has_seen_trial_ending_modal: true
      });
    } else {
      update({
        has_seen_trial_ended_modal: true
      });
    }
    setIsOpen(false);
  };
  const onClose = () => {
    handleClose();
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'dismissed',
        location: analyticsLocation,
        modal_name: modalName,
        path: pathname
      }
    });
  };
  const onCompareAll = () => {
    handleClose();
    dispatch(setAnalyticsLocation(analyticsLocation));
    dispatch(moveToCheckout({
      isDowngrading: false,
      analyticCheckoutFlowLocation: analyticsLocation
    }));
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'opening',
        location: analyticsLocation,
        modal_name: MODAL_NAMES.UPGRADE_PLAN,
        path: pathname
      }
    });
  };
  if (!isTrialLossExperimentActive) return null;
  const trialStatusAsset = isTrialActive ? TrialExpiring : TrialExpired;
  const trialStatusToken = isTrialActive ? 'trial_ending' : 'trial_ended';

  // i18n-tasks-use t('publisher.trial_loss.trial_ended.subtitle')
  // i18n-tasks-use t('publisher.trial_loss.trial_ended.title')
  // i18n-tasks-use t('publisher.trial_loss.trial_ending.subtitle')
  // i18n-tasks-use t('publisher.trial_loss.trial_ending.title')
  // i18n-tasks-use t('publisher.trial_loss.trial_ended.continue_button')
  // i18n-tasks-use t('publisher.trial_loss.trial_ending.continue_button')
  return <Modal isOpen={isOpen}>
      <Wizard>
        {!isPhone && <Header>
            <CalendlyLogoImg src={CalendlyLogo} alt={I18n.t('publisher.trial_loss.calendly_logo')} />
            {!forceCtaInteraction && <CloseButton onClick={onClose}>
                <CloseIcon size="large" />
              </CloseButton>}
          </Header>}
        <Body>
          {isPhone && !forceCtaInteraction && <MobileCloseButton onClick={onClose}>
              <CloseIcon size="large" />
            </MobileCloseButton>}
          {!isSmallPhone && <TrialStatusImg src={trialStatusAsset} />}
          <TitleContainer>
            <HeadingText level={2}>{I18n.t(`publisher.trial_loss.${trialStatusToken}.title`, {
              count: trialEndsInDays
            })}</HeadingText>
            <BodyText>{I18n.t(`publisher.trial_loss.${trialStatusToken}.subtitle`)}</BodyText>
          </TitleContainer>
          <FeatureGrid featureData={featureData} userEnabledFeatures={userEnabledFeatures} />
        </Body>
        <ButtonContainer>
          <CompareButton onClick={onCompareAll}>
            {I18n.t('publisher.trial_loss.compare_button')}
          </CompareButton>
          <ContinueButton onClick={onClose}>
            {I18n.t(`publisher.trial_loss.${trialStatusToken}.continue_button`)}
          </ContinueButton>
        </ButtonContainer>
      </Wizard>
    </Modal>;
};
export const TrialLossModal = ({
  isTrialActive,
  trialEndsInDays
}) => {
  const {
    data: featureData = {},
    isLoading
  } = useGetFeatureUsageQuery({});
  const noFeatures = Object.keys(featureData).length === 0 || Object.values(featureData).every(isValueEmpty);
  if (isLoading || noFeatures) {
    return null;
  }
  return <ModalComponent featureData={featureData} isTrialActive={isTrialActive} trialEndsInDays={trialEndsInDays} />;
};

require("./trial_loss_modal.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./trial_loss_modal.js");